import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { fieldsHelper } from '@helpers';
import { SourceButtonOption, SourceField } from '@interfaces';
import { LanguageService } from '@services';
import { Subject, takeUntil } from 'rxjs';
import { urlValidator } from '../../../core/utils/validators';
import { FieldName, FIELDS } from '../../../core/constants/fields';

@Component({
  selector: 'app-source-repeater',
  templateUrl: './sources.component.html',
  styleUrls: ['./sources.component.scss'],
  animations: [
    trigger('sourceAnimation', [
      state('visible', style({ opacity: 1, transform: 'translateY(0)' })),
      state('hidden', style({ opacity: 0, transform: 'translateY(-20px)' })),

      transition('visible => hidden', [
        animate('0.3s ease-in', style({ opacity: 0, transform: 'translateY(-20px)' })),
      ]),

      transition('hidden => visible', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('0.3s ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),

      transition('void => visible', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('0.3s ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),

      transition('visible => void', [
        animate('0.3s ease-in', style({ opacity: 0, transform: 'translateY(-20px)' })),
      ]),
    ]),
  ],
})
export class SourcesComponent implements OnDestroy {
  @Input() parentForm: FormGroup;
  @Input() fieldTree: any[];
  @Input() sourceButtons: { [key: string]: SourceButtonOption };

  public sourceFormStructure: SourceField[] = [];
  public sourceStates = new Map<string, 'visible' | 'hidden'>();
  public notices = [];

  private destroy$ = new Subject<void>();
  public selectedLanguage = 'en';

  public convertSnakeCaseToSentenceCase = fieldsHelper.convertSnakeCaseToSentenceCase;
  public getTranslatedLabel = fieldsHelper.getTranslatedLabel;
  public evaluateCondition = fieldsHelper.evaluateCondition;
  public findControl = fieldsHelper.findControl;
  public getFieldValue = fieldsHelper.getFieldValue;
  public isRequired = fieldsHelper.isRequired;
  public isHidden = fieldsHelper.isHidden;
  public getPlaceholder = fieldsHelper.getPlaceholder;

  constructor(
    private formBuilder: FormBuilder,
    private cd: ChangeDetectorRef,
    private languageService: LanguageService,
  ) {
    this.languageService.selectedLanguage$.pipe(takeUntil(this.destroy$)).subscribe((lang) => {
      this.selectedLanguage = lang;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getFieldMaxLength(field: FieldName) {
    return FIELDS[field];
  }

  public async handleAdd(type: string) {
    const sourceOptionsControl = this.parentForm.get('source_options_group.source_options');

    if (sourceOptionsControl?.pristine) {
      sourceOptionsControl.markAsDirty();
    }

    if (sourceOptionsControl && !((sourceOptionsControl?.value as string[]) ?? []).includes(type)) {
      sourceOptionsControl.setValue([...(sourceOptionsControl?.value ?? []), type]);
    }

    let sourceKey: string;

    switch (type) {
      case 'eyewitness_account':
      case 'secondary_account':
        if (!this.hasSourceType(type)) {
          const newSource = {
            type: type,
            globalIndex: this.sourceFormStructure.length,
            localIndex: 0,
          };
          sourceKey = this.getSourceKey(newSource);

          this.sourceFormStructure.push(newSource);
          this.sourceStates.set(sourceKey, 'visible');
        }
        break;

      case 'news_media': {
        const path = 'sources.news_media.news_media_object';
        const newsArray = this.parentForm.get(path) as FormArray;
        const localIndex = newsArray.length;

        newsArray.push(
          this.formBuilder.group({
            news_article_url: [
              '',
              [Validators.required, Validators.maxLength(FIELDS['url_max_length']), urlValidator()],
            ],
            publication_name: ['', Validators.maxLength(FIELDS['short_input_max_length'])],
          }),
        );

        const newSource = {
          type: 'news_media',
          globalIndex: this.sourceFormStructure.length,
          localIndex,
        };
        sourceKey = this.getSourceKey(newSource);

        this.sourceFormStructure.push(newSource);
        this.sourceStates.set(sourceKey, 'visible');
        break;
      }

      case 'social_media': {
        const path = 'sources.social_media.social_media_object';
        const socialArray = this.parentForm.get(path) as FormArray;
        const localIndex = socialArray.length;

        socialArray.push(
          this.formBuilder.group({
            post_url: [
              '',
              [Validators.required, Validators.maxLength(FIELDS['url_max_length']), urlValidator()],
            ],
            platform: ['', Validators.maxLength(FIELDS['short_input_max_length'])],
          }),
        );

        const newSource = {
          type: 'social_media',
          globalIndex: this.sourceFormStructure.length,
          localIndex,
        };
        sourceKey = this.getSourceKey(newSource);

        this.sourceFormStructure.push(newSource);
        this.sourceStates.set(sourceKey, 'visible');
        break;
      }

      case 'official_document': {
        const path = 'sources.official_document.official_document_object';
        const docArray = this.parentForm.get(path) as FormArray;
        const localIndex = docArray.length;

        docArray.push(
          this.formBuilder.group({
            document_type: ['', Validators.required],
            document_reference: ['', Validators.maxLength(FIELDS['short_input_max_length'])],
          }),
        );

        const newSource = {
          type: 'official_document',
          globalIndex: this.sourceFormStructure.length,
          localIndex,
        };
        sourceKey = this.getSourceKey(newSource);

        this.sourceFormStructure.push(newSource);
        this.sourceStates.set(sourceKey, 'visible');
        break;
      }
    }

    this.cd.detectChanges();
  }

  public async handleRemove(event: Event, globalIndex: number) {
    event.preventDefault();

    const sourceToRemove = this.sourceFormStructure[globalIndex];
    const sourceKey = this.getSourceKey(sourceToRemove);

    this.sourceStates.set(sourceKey, 'hidden');
    this.cd.detectChanges();

    await this.delay(300);

    if (this.sourceButtons[sourceToRemove.type].repeatable) {
      const path = `sources.${sourceToRemove.type}.${sourceToRemove.type}_object`;
      const sourceArray = this.getControlAsArray(path);
      sourceArray.removeAt(sourceToRemove.localIndex);
    } else {
      const path = `sources.${sourceToRemove.type}`;
      const sourceGroup = this.getControlAsGroup(path);
      Object.keys(sourceGroup.controls).forEach((key) => {
        sourceGroup.controls[key].reset();
      });
    }

    if (sourceToRemove.localIndex === 0) {
      const sourceOptionsControl = this.parentForm.get('source_options_group.source_options');
      if (sourceOptionsControl) {
        sourceOptionsControl.setValue(
          (sourceOptionsControl.value as string[]).filter((value) => value != sourceToRemove.type),
        );
      }
    }

    // Remove from structure and clean up states
    this.sourceFormStructure.splice(globalIndex, 1);
    this.sourceStates.delete(sourceKey);

    // Update indices and animation states
    this.sourceFormStructure.forEach((source, index) => {
      const oldKey = this.getSourceKey(source);

      if (source.type === sourceToRemove.type && source.localIndex > sourceToRemove.localIndex) {
        source.localIndex -= 1;
      }
      source.globalIndex = index;

      const newKey = this.getSourceKey(source);

      // Update state mapping if key changed
      if (oldKey !== newKey) {
        const newState = this.sourceStates.get(oldKey) || 'visible';
        this.sourceStates.delete(oldKey);
        this.sourceStates.set(newKey, newState);
      }
    });

    this.cd.detectChanges();
  }

  public getSourceAnimationState(source: SourceField): string {
    const sourceKey = this.getSourceKey(source);
    return this.sourceStates.get(sourceKey) || 'visible';
  }

  private getSourceKey(source: SourceField): string {
    return `${source.type}_${source.localIndex}_${source.globalIndex}`;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private hasSourceType(type: string): boolean {
    return this.sourceFormStructure.some((source) => source.type === type);
  }

  public canAdd(type: string): boolean {
    return this.sourceButtons[type]?.repeatable || !this.hasSourceType(type);
  }

  public findFieldInTree(tree: any[], fieldName: string): any {
    for (const field of tree) {
      if (field.name === fieldName) {
        return field;
      }
      if (field.children) {
        const found = this.findFieldInTree(field.children, fieldName);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  public getControlAsGroup(path: string) {
    return this.parentForm.get(path) as FormGroup;
  }

  public getControlAsArray(path: string) {
    return this.parentForm.get(path) as FormArray;
  }

  public getSourceButtonValues(): SourceButtonOption[] {
    return Object.values(this.sourceButtons);
  }
}
